import { TokenService } from '@euler/lib/services';
import { createSSEConnection, SSEConnection, SSEEvent } from './SSEClient';

export interface AuthorizedSSEOptions {
  /** The server origin, e.g. `config.aiEndpoint`. */
  endpoint: string;
  /**
   * ABSOLUTE, with the service prefix (`/api/ai/...`): this is a raw URL,
   * not one an `HttpClient` prefixes. Getting that wrong 404s the stream
   * and nothing else notices.
   */
  path: string;
  body?: unknown;
  tokenService: TokenService;
  refreshToken?: () => Promise<string | null>;
  onEvent: (event: SSEEvent) => void;
  onError: (message: string) => void;
}

/**
 * A POSTed SSE stream with the user's token, reconnected once on a 401
 * with a refreshed one: an expired token must refresh and retry rather than
 * surface as a dead stream.
 *
 * A stream that closes without ever delivering `done` or `error` has
 * failed, however cleanly the socket ended; without that the screen waits
 * on a terminal event that is not coming.
 */
export function createAuthorizedSSEConnection(
  options: AuthorizedSSEOptions,
): SSEConnection {
  let connection: SSEConnection | null = null;
  let aborted = false;
  let settled = false;

  const connect = (token: string) => {
    if (aborted) return;
    connection = createSSEConnection({
      url: `${options.endpoint}${options.path}`,
      method: 'POST',
      headers: {
        Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`,
      },
      body:
        options.body === undefined ? undefined : JSON.stringify(options.body),
      onEvent: event => {
        if (event.event === 'done' || event.event === 'error') {
          settled = true;
        }
        try {
          options.onEvent(event);
        } catch (e: any) {
          settled = true;
          options.onError(e.message ?? 'Failed to parse event');
        }
      },
      onComplete: () => {
        if (!settled && !aborted) {
          settled = true;
          options.onError('连接中断，请重试');
        }
      },
      onError: error => {
        if (error.status === 401 && options.refreshToken != null) {
          void options.refreshToken().then(newToken => {
            if (newToken && !aborted) {
              connect(newToken);
            } else {
              options.onError('登录已过期，请重新登录');
            }
          });
          return;
        }
        settled = true;
        options.onError(error.message);
      },
    });
  };

  void options.tokenService.getToken().then(token => connect(token ?? ''));

  return {
    abort: () => {
      aborted = true;
      connection?.abort();
    },
  };
}
